import { Dispatch, SetStateAction, memo } from 'react';
import { Grid, Text } from '@mantine/core';
import moment from 'moment';
import { type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import Table from '../../../components/Table';
import { formatMoneyString } from '../../../utils/strings';

export default memo(function TableView({
  tableInfo = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination,
  footerInfo = {
    totalAmountIn: 0,
    totalAmountOut: 0
  }
}: {
  tableInfo: any[];
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
  footerInfo: {
    totalAmountIn: number;
    totalAmountOut?: number;
  };
}) {
  const { t } = useTranslation(['FieldName']);

  const fallbackTotals = tableInfo.reduce(
    (acc, row) => {
      const amount = Number(row?.amount ?? 0);
      if (row?.type === 'in') {
        acc.totalAmountIn += amount;
      } else {
        acc.totalAmountOut += amount;
      }
      return acc;
    },
    { totalAmountIn: 0, totalAmountOut: 0 }
  );

  const totalAmountIn = Number(footerInfo?.totalAmountIn ?? fallbackTotals.totalAmountIn);
  const totalAmountOut = Number(footerInfo?.totalAmountOut ?? fallbackTotals.totalAmountOut);

  const columns = [
    {
      accessorFn: (row: any) => row?.cashierBox?.name,
      header: t('FieldName:cashierBox')
    },
    {
      accessorFn: (row: any) => t(`FieldName:cashierBoxMovement.${row.type}`),
      header: t('FieldName:type')
    },
    {
      accessorFn: (row: any) => t(`FieldName:paymentMethods.${row.paymentMethod}`),
      header: t('FieldName:paymentMethod')
    },
    {
      accessorFn: (row: any) => row?.type === 'in' ? formatMoneyString((row.amount || 0).toFixed(2)) : formatMoneyString('0.00'),
      header: t('FieldName:amountIn'),
      Footer: () => (
        <Grid>
          <Grid.Col span={12}>
            <Text fw="bold">{formatMoneyString(totalAmountIn.toFixed(2))}</Text>
          </Grid.Col>
        </Grid>
      )
    },
    {
      accessorFn: (row: any) => row?.type === 'out' ? formatMoneyString((row.amount || 0).toFixed(2)) : formatMoneyString('0.00'),
      header: t('FieldName:amountOut'),
      Footer: () => (
        <Grid>
          <Grid.Col span={12}>
            <Text fw="bold">{formatMoneyString(totalAmountOut.toFixed(2))}</Text>
          </Grid.Col>
        </Grid>
      )
    },
    {
      accessorFn: (row: any) => moment(row.date).format('DD/MM/YYYY HH:mm'),
      header: t('FieldName:date')
    },
    {
      accessorFn: (row: any) =>
        row.isOnACashUpReport ? t('FieldName:yes') : t('FieldName:no'),
      header: t('FieldName:isOnACashUpReport')
    },
    {
      accessorFn: (row: any) => row.notes,
      header: t('FieldName:notes')
    },
    {
      accessorFn: (row: any) => row?.createdBy?.name,
      header: t('FieldName:createdBy')
    }
  ];

  return (
    <Grid gutter="md">
      <Grid.Col span={12}>
        <Table
          tableColumns={columns}
          pagination={pagination}
          setPagination={setPagination}
          tableData={tableInfo}
          isError={isError}
          paginationIsLoading={isLoading}
          numPages={pages}
          totalRowsCount={totalRowsCount}
          overrideTableConfigs={{}}
        />
      </Grid.Col>
    </Grid>
  );
});
